import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { motion } from "framer-motion";
import { Star, Send } from "lucide-react";
import { toast } from "sonner";
import { locations } from "../data/locations";

const Feedback = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    location: "",
    rating: 0,
    message: "",
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const captchaRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    if (submitted) setSubmitted(false);
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captcha) {
      toast.error("Please complete the CAPTCHA first.");
      return;
    }
    if (!form.name || !form.email || !form.location || form.rating === 0 || !form.message) {
      toast.error("Please complete all fields, including a star rating.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/_/backend/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, captcha }),
      });
      if (!res.ok) throw new Error("Server error");
      setSubmitted(true);
      setHoverRating(0);
      setForm({ name: "", email: "", location: "", rating: 0, message: "" });
      toast.success("Feedback received! Thank you.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      captchaRef.current?.reset();
      setCaptcha(null);
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="feedback-page" className="bg-cream">
      {/* Hero */}
      <section className="bg-navy hero-pattern clip-diagonal-tr py-20">
        <div className="mx-auto max-w-5xl px-6 text-center lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="font-ui text-sm text-cream/80">Tell Us How We Did</div>
            <h1
              className="font-display mt-2 text-gold"
              style={{ fontSize: "clamp(2.6rem, 6vw, 5rem)", lineHeight: 0.95 }}
              data-testid="feedback-title"
            >
              WE APPRECIATE YOUR FEEDBACK!
            </h1>
            <p className="font-body mx-auto mt-5 max-w-xl text-cream/85">
              Good, bad, or somewhere in between — your honest take helps us
              keep the kitchen humming.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="mx-auto max-w-3xl px-6 py-20 lg:px-8">
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          onSubmit={handleSubmit}
          className="rounded-md bg-white p-8 shadow-lg"
          style={{ borderTop: "4px solid var(--red)" }}
          data-testid="feedback-form"
        >
          <h2 className="font-display text-3xl text-navy">SHARE YOUR EXPERIENCE</h2>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="fname">Name</label>
              <input
                id="fname"
                name="name"
                className="field"
                value={form.name}
                onChange={handleChange}
                placeholder="Your name"
                data-testid="feedback-input-name"
              />
            </div>
            <div>
              <label className="label" htmlFor="femail">Email</label>
              <input
                id="femail"
                name="email"
                type="email"
                className="field"
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                data-testid="feedback-input-email"
              />
            </div>
          </div>

          <div className="mt-4">
            <div>
              <label className="label" htmlFor="floc">Location Visited</label>
              <select
                id="floc"
                name="location"
                className="field"
                value={form.location}
                onChange={handleChange}
                data-testid="feedback-input-location"
              >
                <option value="">Select a location</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-5">
            <div className="label">Your Rating</div>
            <div className="flex items-center gap-2" data-testid="feedback-stars">
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = (hoverRating || form.rating) >= n;
                return (
                  <button
                    type="button"
                    key={n}
                    aria-label={`Rate ${n} out of 5`}
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setForm({ ...form, rating: n })}
                    className="transition-transform hover:scale-110"
                    data-testid={`feedback-star-${n}`}
                  >
                    <Star
                      size={32}
                      strokeWidth={2}
                      color={filled ? "var(--gold)" : "var(--navy)"}
                      fill={filled ? "var(--gold)" : "none"}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5">
            <label className="label" htmlFor="fmsg">Your Feedback</label>
            <textarea
              id="fmsg"
              name="message"
              rows={6}
              className="field"
              value={form.message}
              onChange={handleChange}
              placeholder="Tell us about your visit..."
              data-testid="feedback-input-message"
            />
          </div>

          <div className="mt-6" data-testid="feedback-captcha">
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={setCaptcha}
              onExpired={() => setCaptcha(null)}
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !captcha}
            className="btn-primary mt-4 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="feedback-submit"
          >
            {submitting ? "Submitting..." : "Submit Feedback"} <Send size={16} />
          </button>

          {submitted ? (
            <div
              className="font-ui mt-4 rounded-sm px-4 py-3 text-sm"
              style={{
                background: "rgba(245,197,24,0.16)",
                border: "1px solid rgba(27,43,107,0.2)",
                color: "var(--navy)",
              }}
              data-testid="feedback-success-message"
            >
              Message received. Our team will review your feedback shortly.
            </div>
          ) : null}
        </motion.form>
      </section>
    </div>
  );
};

export default Feedback;
